import { memo, useCallback, useMemo, useState } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import _ from 'lodash';
import { fieldMappingSelector, fieldsSelector } from 'slices/field.slice';
import {
  checkFieldRelationSaveDisabled,
  createFieldRelation,
  deleteFieldRelation,
  fieldRelationMappingSelector,
  fieldRelationsSelector,
  firstFieldRelationSelector,
  secondFieldRelationSelector,
  setFirstFieldRelation,
  setSecondFieldRelation,
  updateFieldRelation,
} from 'slices/fieldRelation.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { errorNotification } from 'utils/notifications';
import {
  EFieldType,
  EMatchMode,
  EOperator,
  type TField,
  type TFieldRelation,
} from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import FieldRelation from './FieldRelation';

interface FieldRelationDrawerProps {
  confirmDiscardFieldRelationChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const FieldRelationsDrawer = ({
  confirmDiscardFieldRelationChanges,
}: FieldRelationDrawerProps) => {
  const dispatch = useAppDispatch();

  const firstFieldRelation: TFieldRelation = useAppSelector(
    firstFieldRelationSelector
  );
  const secondFieldRelation: TFieldRelation = useAppSelector(
    secondFieldRelationSelector
  );
  const fieldRelationMapping: Record<number | string, TFieldRelation> =
    useAppSelector(fieldRelationMappingSelector);
  const allFields: TField[] = useAppSelector(fieldsSelector);
  const fieldMapping: Record<number, TField> =
    useAppSelector(fieldMappingSelector);
  const fieldRelations: TFieldRelation[] = useAppSelector(
    fieldRelationsSelector
  );

  const isFirstFieldRelationSaveDisabled = useMemo(
    () => dispatch(checkFieldRelationSaveDisabled(true)),
    [firstFieldRelation]
  );
  const isSecondFieldRelationSaveDisabled = useMemo(
    () => dispatch(checkFieldRelationSaveDisabled(false)),
    [secondFieldRelation]
  );

  const setFirstResource = useCallback(
    (fieldRelation: TFieldRelation) =>
      dispatch(setFirstFieldRelation(fieldRelation)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (fieldRelation: TFieldRelation) =>
      dispatch(setSecondFieldRelation(fieldRelation)),
    [dispatch]
  );

  useSelectResource(
    'integer',
    fieldRelationMapping,
    firstFieldRelation,
    secondFieldRelation,
    setFirstResource,
    setSecondResource
  );

  const onClose = (isFirstDrawer: boolean) => {
    confirmDiscardFieldRelationChanges(isFirstDrawer, () => {
      if (isFirstDrawer) {
        dispatch(setFirstFieldRelation(null));
      } else {
        dispatch(setSecondFieldRelation(null));
      }
    });
  };

  // State and function for checking if the combination of fieldTypes is a valid FieldRelation combination
  const [invalidFieldRelation, setInvalidFieldRelation] = useState(false);
  const checkValidFieldRelation = useCallback(
    (firstType: string | undefined, secondType: string | undefined) => {
      if (
        firstType !== EFieldType.INTEGER &&
        secondType === EFieldType.INTEGER
      ) {
        setInvalidFieldRelation(true);
      } else if (
        firstType !== EFieldType.CHECKBOX &&
        secondType === EFieldType.CHECKBOX
      ) {
        setInvalidFieldRelation(true);
      } else if (
        (firstType === EFieldType.CHECKBOX &&
          secondType === EFieldType.CATEGORY) ||
        (firstType === EFieldType.INTEGER && secondType === EFieldType.CATEGORY)
      ) {
        setInvalidFieldRelation(true);
      } else {
        setInvalidFieldRelation(false);
      }
    },
    []
  );

  const onChange = (
    isFirstFieldRelation: boolean,
    property: string,
    value: any
  ) => {
    const onChangeFn = (fr: TFieldRelation) =>
      isFirstFieldRelation
        ? dispatch(setFirstFieldRelation(fr))
        : dispatch(setSecondFieldRelation(fr));
    const fieldRelation = isFirstFieldRelation
      ? firstFieldRelation
      : secondFieldRelation;
    if (property === 'sourceField' || property === 'matchField') {
      // Figure out if the field type of the selected property is category or not
      const selectedField = allFields.find(
        (field: TField) => field.id === value
      );
      const otherProperty =
        property === 'sourceField' ? 'matchField' : 'sourceField';
      const otherField = allFields.find(
        (field: TField) => field.id === fieldRelation[otherProperty]
      );
      dispatch(
        onChangeFn({
          ...fieldRelation,
          [property]: value,
          matchMode: selectMatchMode(
            selectedField?.fieldType,
            otherField?.fieldType
          ),
          operator: selectOperator(
            selectedField?.fieldType,
            otherField?.fieldType
          ),
        })
      );
      checkValidFieldRelation(selectedField?.fieldType, otherField?.fieldType);
    } else {
      dispatch(
        onChangeFn({
          ...fieldRelation,
          [property]: value,
        })
      );
    }
  };

  const onSave = useCallback((fieldRelation: TFieldRelation) => {
    const sourceField = fieldMapping[fieldRelation.sourceField!];
    const matchField = fieldMapping[fieldRelation.matchField!];
    if (
      sourceField &&
      matchField &&
      sourceField.fieldType === EFieldType.INTEGER &&
      matchField.fieldType === EFieldType.INTEGER &&
      !fieldRelation.operator
    ) {
      return errorNotification(
        'Operator should be selected for Integer fields'
      );
    }
    if (fieldRelation.id === 0) {
      if (
        _.some(
          fieldRelations,
          (fr: TFieldRelation) => fr.name === fieldRelation.name && fr.id !== 0
        )
      ) {
        errorNotification(getText('name_field_relation_already_exists'));
      } else {
        dispatch(createFieldRelation(fieldRelation));
      }
    } else {
      dispatch(updateFieldRelation(fieldRelation));
    }
  }, []);

  return (
    <TEDrawer
      expandWidth="70vw"
      firstDrawer={{
        title: _.get(firstFieldRelation, 'name', ''),
        content: (
          <FieldRelation
            fieldRelation={firstFieldRelation}
            onChange={(key: string, value: any) => onChange(true, key, value)}
          />
        ),
        drawerOpen: !!firstFieldRelation,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('field_relation_confirmation_delete')}
            handleDelete={() =>
              dispatch(deleteFieldRelation(firstFieldRelation.id))
            }
            handleDiscard={() =>
              dispatch(
                setFirstFieldRelation(
                  fieldRelationMapping[firstFieldRelation.id]
                )
              )
            }
            handleSave={() => onSave(firstFieldRelation!)}
            isSaveDisable={isFirstFieldRelationSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => {
          if (!open) onClose(true);
        },
      }}
      secondDrawer={{
        title: _.get(secondFieldRelation, 'name', ''),
        content: (
          <FieldRelation
            fieldRelation={secondFieldRelation}
            onChange={(key: string, value: any) => onChange(false, key, value)}
          />
        ),
        drawerOpen: !!secondFieldRelation,
        extra: (
          <DrawerExtraButtons
            confirmDeleteMsg={getText('padding_rule_confirmation_delete')}
            handleDelete={() =>
              dispatch(deleteFieldRelation(secondFieldRelation.id))
            }
            handleDiscard={() =>
              dispatch(
                setSecondFieldRelation(
                  fieldRelationMapping[secondFieldRelation.id]
                )
              )
            }
            handleSave={() => onSave(secondFieldRelation!)}
            isSaveDisable={isSecondFieldRelationSaveDisabled as any}
          />
        ),
        setDrawerOpen: (open) => {
          if (!open) onClose(false);
        },
      }}
      onExpand={() => null}
    />
  );
};

export default memo(FieldRelationsDrawer);

// Function for setting correct MatchMode based on fieldTypes
// in source- and matchField
const selectMatchMode = (
  firstType: string | undefined,
  secondType: string | undefined
) => {
  if (firstType === EFieldType.CATEGORY && secondType === EFieldType.CATEGORY) {
    return EMatchMode.MATCH_ONE;
  }
  return null;
};

// Function for setting correct Operator based on fieldTypes
// in source- and matchField
const selectOperator = (
  firstType: string | undefined,
  secondType: string | undefined
) => {
  if (firstType === EFieldType.INTEGER && secondType === EFieldType.INTEGER) {
    return EOperator.E;
  }
  return null;
};
